import React from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table';
import { 
  ChevronLeftIcon, 
  ChevronRightIcon,
  ArrowUpIcon,
  ArrowDownIcon 
} from '@heroicons/react/24/outline';

const DataTable = ({
  data,
  columns,
  pageSize = 50,
  onRowClick,
  loading = false,
  exportOptions = true,
  total = 0,
  page = 1,
  onPageChange,
  onPageSizeChange,
  onExport
}) => {
  // Calculate total pages
  const totalPages = total > 0 ? Math.ceil(total / pageSize) : Math.ceil(data.length / pageSize);
  
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: total > 0, // Enable manual pagination if total is provided
    pageCount: totalPages,
    state: {
      pagination: {
        pageIndex: page - 1,
        pageSize,
      },
    },
    onPaginationChange: (updater) => {
      let newState;
      if (typeof updater === 'function') {
        newState = updater({ pageIndex: page - 1, pageSize });
      } else {
        newState = updater;
      }
      
      // Call parent handlers if provided
      if (onPageChange) {
        onPageChange(newState.pageIndex + 1);
      }
      if (onPageSizeChange) {
        onPageSizeChange(newState.pageSize);
      }
      
      // Update internal state
      table.setOptions((prev) => ({
        ...prev,
        state: {
          ...prev.state,
          pagination: newState,
        },
      }));
    },
  });

  const handleExport = (format) => {
    if (onExport) {
      onExport(format);
    } else {
      console.log(`Exporting as ${format}`);
    }
  };

  const getSortingIcon = (column) => {
    const sorted = column.getIsSorted();
    if (sorted === 'asc') {
      return <ArrowUpIcon className="h-4 w-4 ml-1 inline" />;
    }
    if (sorted === 'desc') {
      return <ArrowDownIcon className="h-4 w-4 ml-1 inline" />;
    }
    return null;
  };

  const currentPage = table.getState().pagination.pageIndex + 1;
  const canPrevious = currentPage > 1;
  const canNext = currentPage < totalPages;

  const handlePrevious = () => {
    if (canPrevious) {
      const newPage = currentPage - 1;
      table.setPageIndex(newPage - 1);
      if (onPageChange) {
        onPageChange(newPage);
      }
    }
  };

  const handleNext = () => {
    if (canNext) {
      const newPage = currentPage + 1;
      table.setPageIndex(newPage - 1);
      if (onPageChange) {
        onPageChange(newPage);
      }
    }
  };

  return (
    <div className="space-y-4">
      {exportOptions && (
        <div className="flex justify-end space-x-2" role="group" aria-label="Export options">
          <button
            onClick={() => handleExport('csv')}
            className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            aria-label="Export as CSV"
          >
            CSV
          </button>
          <button
            onClick={() => handleExport('excel')}
            className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            aria-label="Export as Excel"
          >
            Excel
          </button>
          <button
            onClick={() => handleExport('pdf')}
            className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            aria-label="Export as PDF"
          >
            PDF
          </button>
          <button
            onClick={() => handleExport('print')}
            className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            aria-label="Print table"
          >
            Print
          </button>
        </div>
      )}

      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200" role="table">
          <thead className="bg-gray-50">
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <th
                    key={header.id}
                    onClick={header.column.getToggleSortingHandler()}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors duration-150"
                    aria-sort={header.column.getIsSorted() || 'none'}
                  >
                    <div className="flex items-center">
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                      {getSortingIcon(header.column)}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={columns.length} className="px-6 py-8 text-center">
                  <div className="flex justify-center items-center space-x-2">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                    <span className="text-sm text-gray-500">Loading...</span>
                  </div>
                </td>
              </tr>
            ) : table.getRowModel().rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-6 py-8 text-center text-gray-500">
                  <div className="flex flex-col items-center">
                    <svg className="h-12 w-12 text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="text-sm">No data available</p>
                  </div>
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map(row => (
                <tr
                  key={row.id}
                  onClick={() => onRowClick?.(row.original)}
                  className={`${
                    onRowClick ? 'cursor-pointer hover:bg-gray-50 transition-colors duration-150' : ''
                  }`}
                >
                  {row.getVisibleCells().map(cell => (
                    <td key={cell.id} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-700">
            Page {currentPage} of {totalPages || 1}
            {total > 0 && ` (${total.toLocaleString()} total records)`}
          </span>
          <select
            value={pageSize}
            onChange={(e) => {
              const newSize = Number(e.target.value);
              if (onPageSizeChange) {
                onPageSizeChange(newSize);
              }
              table.setPageSize(newSize);
            }}
            className="ml-4 px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            aria-label="Rows per page"
          >
            {[5, 10, 20, 50, 100, 500, 1000].map(size => (
              <option key={size} value={size}>
                Show {size}
              </option>
            ))}
          </select>
        </div>

        <div className="flex gap-2">
          <button
            onClick={handlePrevious}
            disabled={!canPrevious}
            className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            aria-label="Previous page"
          >
            <ChevronLeftIcon className="h-5 w-5" />
          </button>
          
          {/* Page numbers for better UX */}
          <div className="hidden sm:flex items-center gap-1">
            {[...Array(Math.min(5, totalPages))].map((_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              
              return (
                <button
                  key={pageNum}
                  onClick={() => {
                    table.setPageIndex(pageNum - 1);
                    if (onPageChange) {
                      onPageChange(pageNum);
                    }
                  }}
                  className={`px-3 py-1 text-sm rounded-md ${
                    currentPage === pageNum
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>
          
          <button
            onClick={handleNext}
            disabled={!canNext}
            className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            aria-label="Next page"
          >
            <ChevronRightIcon className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default DataTable;